/**
 * EA Cart Lock — "does the cart currently hold store-bound items?"
 *
 * A cart is fulfilled from ONE store, so once it holds a store-bound item the
 * shopper can no longer change the Zip Code, the fulfillment type or the Store —
 * those choices are already baked into the line items (`item_fulfillment_type`)
 * and the cart attributes (`store_id`, `order_type`). Clearing the cart releases
 * the lock.
 *
 * This module owns that one boolean and nothing else. Every surface (header
 * modal, PDP availability block, and the shared clear-store control the Bundle
 * Builder drawer also uses) reads `isCartStoreActive()` and listens for
 * `ea:cart-store-active`.
 *
 * On-Goody products ship free with no local store, and the cart-conflict rules
 * already keep them from sharing a cart with normal products — so an On-Goody
 * cart is NOT store-bound and must not lock anything.
 *
 * State lives in its OWN localStorage key, deliberately not inside
 * `ea-store-availability`: that object is the header's selection contract and
 * every key in it is one the header reads back. Same reasoning as
 * `ea-max-fulfillment-option`.
 */

import {
  CART_ITEM_TYPES,
  CART_PROPERTY_KEYS,
  cartHasMerchandise,
  clearedGratuityAttributesPayload,
  gratuityLifecycleAction,
  isCompleteDeliveryContext,
  isStoreFulfilledLine,
  isSurchargeItem,
  readLineDeliveryContext,
} from '@theme/cart-contract';
import {
  clearedCartAttributesPayload,
  entryAvailableFor,
  schedulePickupPointPreferenceSync,
  todayIso,
} from '@theme/ea-availability';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/** localStorage key holding the lock state (`'1'` / `'0'`). */
export const CART_STORE_ACTIVE_KEY = 'ea-cart-store-active';

/** Document event fired when the lock flips. `detail: { active: boolean }`. */
export const CART_STORE_ACTIVE_EVENT = 'ea:cart-store-active';

/**
 * The SECOND, narrower signal: "the cart holds anything at all", Goody included.
 *
 * Deliberately separate from the store lock above rather than a widening of it. The
 * two answer different questions and have different consumers:
 *
 * - `isCartStoreActive()` — "is the cart bound to a STORE?" Goody carts are not, and
 *   @theme/goody-fulfillment-gate depends on that exact meaning to tell a compatible
 *   cart from an incompatible one. Widening it makes every repeat Goody add look like
 *   a conflict and prompt the shopper to clear a cart they just filled.
 * - `isCartNonEmpty()` — "may the ZIP still be edited?" It may not, for any cart with
 *   items: the zip is stamped on every line and on `cart.attributes.zip_code`, and for
 *   Goody it is also the destination the carrier rate was quoted against.
 *
 * Both are derived from the same `/cart.js` read, so the extra signal costs no request.
 */
export const CART_ANY_ITEMS_KEY = 'ea-cart-any-items';

/** Document event fired when the non-empty state flips. `detail: { active: boolean }`. */
export const CART_ANY_ITEMS_EVENT = 'ea:cart-any-items';

/**
 * The THIRD signal: "the cart holds at least one Goody line".
 *
 * The opposite of `isCartStoreActive()` in intent but NOT its negation — a cart
 * holding both kinds would report true on each, and an EMPTY cart reports false on
 * both. That last case is the one that matters: "no Goody lines" is what
 * @theme/goody-fulfillment-recovery waits for before putting the shopper's store
 * selection back, and an empty cart is precisely how a Goody cart ends.
 *
 * Kept as its own signal rather than derived at each call site because the whole
 * point is the TRANSITION, and only this module sees every cart mutation.
 *
 * Derived from the same `/cart.js` read as the other two, so it costs no request.
 */
export const CART_GOODY_ITEMS_KEY = 'ea-cart-goody-items';

/** Document event fired when the Goody state flips. `detail: { active: boolean }`. */
export const CART_GOODY_ITEMS_EVENT = 'ea:cart-goody-items';
/**
 * The FOURTH signal: "the cart holds products but no fulfillment" — P7 client UAT
 * (2026-10-30). /cart?view=ea-change-fulfillment (assets/ea-change-fulfillment.js) rebuilds
 * a cart with the shopper's products and none of the old delivery/pickup choice, then sends
 * them to the header selector to choose again.
 *
 * Such a cart still holds store-fulfilled lines, so `isCartStoreActive()` stays true and
 * keeps its meaning (@theme/goody-fulfillment-gate depends on it). This separate signal is
 * what lets the header selector open UNLOCKED for it: a lock there would leave the shopper
 * with products, no store, and "Clear Cart" as the only way forward. It is derived from the
 * cart, not set once, so closing the selector without choosing never strands the cart, and
 * it ends by itself once a selection is saved (`store_id` is written, the lines restamped).
 */
export const CART_FULFILLMENT_PENDING_KEY = 'ea-cart-fulfillment-pending';
/** Document event fired when the pending state flips. `detail: { active: boolean }`. */
export const CART_FULFILLMENT_PENDING_EVENT = 'ea:cart-fulfillment-pending';

/**
 * Captured before we wrap it, so our own reads can't re-enter the hook below.
 * @type {typeof fetch}
 */
const baseFetch = window.fetch.bind(window);

/** Cart mutations. `/cart.js` (a plain read) deliberately doesn't match. */
const CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

/** @type {boolean|null} Last known value — avoids a localStorage read per call. */
let current = null;

/** True when the cart holds at least one item that needs a store. */
function cartHasStoreItems(cart) {
  // `null` means "couldn't tell", NOT "empty". A 200 that parses to something without
  // an `items` array would otherwise read as an empty cart and release the lock —
  // the same trusting-the-body mistake this module already had once. Callers skip
  // null and keep the last known state.
  if (!cart || !Array.isArray(cart.items)) return null;
  // Entries have to be readable too. A `[null]` would fall through the optional
  // chaining below and count as a store item, locking on data we can't actually read.
  if (cart.items.some((item) => !item || typeof item !== 'object')) return null;
  // EA-4351: the store surcharge is a fee with nothing to deliver, so it never binds
  // the cart to a store — a refresh of a surcharge-only cart must not restore the lock.
  // Mirrors the server seed in snippets/scripts.liquid.
  return cart.items.some(
    (item) =>
      !isSurchargeItem(item) && String(item.properties?.[CART_PROPERTY_KEYS.isOnGoody]) !== 'true',
  );
}

/** Writes the state and announces it, but only when it actually changed. */
function apply(active) {
  const next = !!active;
  const changed = current !== next;
  current = next;
  try {
    localStorage.setItem(CART_STORE_ACTIVE_KEY, next ? '1' : '0');
  } catch (e) {
    // Private mode / storage blocked — `current` still holds for this page.
  }
  if (changed) document.dispatchEvent(new CustomEvent(CART_STORE_ACTIVE_EVENT, { detail: { active: next } }));
}

/**
 * Is the cart store-bound right now?
 * @returns {boolean}
 */
export function isCartStoreActive() {
  if (current !== null) return current;
  try {
    current = localStorage.getItem(CART_STORE_ACTIVE_KEY) === '1';
  } catch (e) {
    current = false;
  }
  return current;
}

/**
 * Seeds the state from the server-rendered cart (see snippets/scripts.liquid), so
 * the first paint is already correct and no surface flashes as unlocked.
 * @param {boolean} active
 */
export function seedCartStoreActive(active) {
  apply(active);
}

/** @type {boolean|null} Last known "cart holds anything at all". */
let currentAny = null;

/**
 * True when the cart holds any line the shopper is BUYING, Goody included. Same
 * `null` contract as cartHasStoreItems — "couldn't tell", never "empty".
 *
 * The gratuity line does not count (EA-3697). This signal exists to lock the ZIP
 * field, and CART_ANY_ITEMS_KEY above gives the reason: the zip is stamped on every
 * line, and for Goody it is the destination a carrier rate was quoted against. A tip
 * carries neither. It is a charge attached to a cart, not a thing being sent
 * somewhere, so it is not a reason to refuse a shopper their ZIP.
 *
 * Left as `items.length > 0`, it made an orphan tip look like a full cart: the
 * lifecycle in this same file was already clearing that cart, but the clear is three
 * round trips behind the load and the lock is applied from the server-rendered seed
 * before any of them land. So the shopper saw a locked ZIP on a cart the drawer was
 * simultaneously rendering as empty — the same paint-order mismatch EA-3876 fixed in
 * the drawer, one signal over.
 *
 * `cartHasMerchandise` is the contract's own answer to this question, so this asks it
 * rather than restating it. Its null contract is marginally stricter — a malformed
 * ITEM also reads "couldn't tell" — which errs the same way: never unlock on a guess.
 */
function cartHasAnyItems(cart) {
  return cartHasMerchandise(cart);
}

/** Writes the non-empty state and announces it, but only when it actually changed. */
function applyAny(active) {
  const next = !!active;
  const changed = currentAny !== next;
  currentAny = next;
  try {
    localStorage.setItem(CART_ANY_ITEMS_KEY, next ? '1' : '0');
  } catch (e) {
    // Private mode / storage blocked — `currentAny` still holds for this page.
  }
  if (changed) document.dispatchEvent(new CustomEvent(CART_ANY_ITEMS_EVENT, { detail: { active: next } }));
}

/**
 * Does the cart hold anything at all? Read by the header's zip field — see
 * CART_ANY_ITEMS_KEY for why this is separate from isCartStoreActive().
 * @returns {boolean}
 */
export function isCartNonEmpty() {
  if (currentAny !== null) return currentAny;
  try {
    currentAny = localStorage.getItem(CART_ANY_ITEMS_KEY) === '1';
  } catch (e) {
    currentAny = false;
  }
  return currentAny;
}

/** @type {boolean|null} Last known "cart holds at least one Goody line". */
let currentGoody = null;

/**
 * True when the cart holds at least one Goody line. Same `null` contract as the two
 * above — "couldn't tell", never "empty" — including the unreadable-entry guard, so a
 * cart we can't parse never reads as "Goody is gone" and triggers a restore.
 */
function cartHasGoodyItems(cart) {
  if (!cart || !Array.isArray(cart.items)) return null;
  if (cart.items.some((item) => !item || typeof item !== 'object')) return null;
  return cart.items.some((item) => String(item.properties?.[CART_PROPERTY_KEYS.isOnGoody]) === 'true');
}

/**
 * One attempt per empty-cart transition, so a failed write cannot spin.
 * Reset the moment merchandise comes back.
 */
let gratuityResetAttempted = false;

/**
 * THE END OF THE TIP LIFECYCLE — EA-3697, QA finding 3 (14 Sep 2026).
 *
 * A Shopify cart outlives its line items: `/cart/clear.js` and removing the last
 * line both leave the cart object, its `note` and ALL of its attributes standing.
 * So the tip decision survived into the next, unrelated cart. Found live on
 * `myedible` in exactly that state — `item_count: 0` alongside
 * `_tip_mode: "percent"`, `_tip_amount: "2933"`, a $29.33 tip computed from
 * merchandise that was no longer there. A shopper who had chosen "No tip" carried
 * `_tip_touched: "true"` + `_tip_mode: "none"` forward, and the checkout
 * extension — correctly, on the state it was given — suppressed the 18% default
 * on a cart that had never been offered one.
 *
 * This is the theme's job and can be nobody else's: the extension only runs in
 * checkout, where there is always at least one line, so "the cart went empty" is
 * an event it is structurally unable to see.
 *
 * WHAT COUNTS AS THE END is "no merchandise", not "no lines". A cart holding
 * nothing but the gratuity line is an empty cart with a charge attached, so the
 * orphan line goes too.
 *
 * Deliberately NOT "reset the tip whenever the cart changes": an explicit choice
 * has to survive quantity edits, added products and a checkout reload. Only the
 * lifecycle ENDING ends it.
 *
 * @param {any} cart a `/cart.js` payload
 */
function endGratuityLifecycle(cart) {
  const action = gratuityLifecycleAction(cart);
  if (!action) {
    // Either the cart is unreadable, or merchandise is still there — in which
    // case the decision stands and the one-attempt guard resets with it.
    if (cartHasMerchandiseNow(cart)) gratuityResetAttempted = false;
    return;
  }
  if (gratuityResetAttempted) return;
  gratuityResetAttempted = true;

  // ONE WRITE, AND ONLY OVER THE LINES THE READ SAW — EA-3876 follow-up.
  //
  // This used to be an unconditional `/cart/clear.js` followed by a separate
  // `/cart/update.js`, and both halves of that were wrong.
  //
  // The RACE: the decision above comes from a `/cart.js` read, and
  // `/cart/clear.js` removes every line in the cart AT THE MOMENT IT LANDS — it
  // cannot tell a line that read saw from one added afterwards. A shopper who
  // added a product in that window had it deleted by a clear aimed at an orphan
  // gratuity line. `{ updates: { <key>: 0 } }` names the lines instead, so the
  // write can only touch what the decision was actually made about.
  //
  // The SECOND WRITE: `/cart/update.js` takes `updates` and `attributes` in one
  // body (the same shape `#applyCartUpdates` in the drawer has always used), so
  // the line removal and the attribute clear land together — one round trip, and
  // no half-cleared cart in between if the page goes away mid-sequence.
  //
  // `/cart/clear.js` survives as the FALLBACK ONLY, for a cart whose lines we
  // could not name (see `orphanLineKeys`): a tip line we cannot address is worse
  // left behind than removed bluntly, since it is a charge on an empty cart.
  const useKeys = action.clearOrphanLine && action.orphanLineKeys.length > 0;
  const needsBlindClear = action.clearOrphanLine && action.orphanLineKeys.length === 0;

  /** @type {{updates?: Record<string, number>, attributes: Record<string, string>}} */
  const payload = { ...clearedGratuityAttributesPayload() };
  if (useKeys) {
    const updates = {};
    for (const key of action.orphanLineKeys) updates[key] = 0;
    payload.updates = updates;
  }

  // `baseFetch`, so our own writes do not re-enter the hook below and restart this.
  Promise.resolve()
    .then(() => (needsBlindClear ? baseFetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }) : null))
    .then(() =>
      baseFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `{ attributes: {} }` is a no-op — only a key sent as '' is removed.
        body: JSON.stringify(payload),
      }),
    )
    .then(() => {
      // THE CLEAR HAS TO ANNOUNCE ITSELF — EA-3697, QA finding 1 (15 Sep 2026).
      //
      // This chain used to end at the write, on the assumption that "the refresh
      // that follows naturally will see the cleared cart". Nothing follows
      // naturally: removing the last merchandise line is the shopper's LAST
      // action, so these writes were the end of the sequence and no surface was
      // ever told the cart had changed underneath it.
      //
      // What QA met, reproduced live on myedible: remove the only product from the
      // cart drawer and the server cart goes to `item_count: 0` with the tip
      // attributes cleared — correctly — while the drawer keeps rendering the line
      // it no longer has as "Gratuity $0.00", with no Remove and no quantity
      // control (both deliberate), no empty-cart state, a header count of 1 and a
      // live Checkout button. A phantom cart holding one item that cannot be
      // removed, which is exactly the "I cannot clear the cart" report.
      //
      // `cart:update` rather than an exemption from the `baseFetch` rule above:
      // it is the theme's own signal and every cart surface already listens for
      // it. Re-entering `refreshCartStoreActive` through the listener below is
      // wanted, not tolerated — the read it does sees an empty cart with no tip
      // state, for which `gratuityLifecycleAction` returns null, so this cannot
      // loop. It is also what puts the LOCK right: the read that got here saw the
      // orphan line and locked the ZIP and store on it, and only a fresh read
      // releases them.
      apply(false);
      applyAny(false);
      applyGoody(false);
      document.dispatchEvent(new CartUpdateEvent(null, 'ea-cart-lock'));
    })
    .catch(() => {
      // The cart is unreachable. The next refresh tries again.
      gratuityResetAttempted = false;
    });
}

/** @param {any} cart @returns {boolean} */
function cartHasMerchandiseNow(cart) {
  return cartHasMerchandise(cart) === true;
}

/** Writes the Goody state and announces it, but only when it actually changed. */
function applyGoody(active) {
  const next = !!active;
  const changed = currentGoody !== next;
  currentGoody = next;
  try {
    localStorage.setItem(CART_GOODY_ITEMS_KEY, next ? '1' : '0');
  } catch (e) {
    // Private mode / storage blocked — `currentGoody` still holds for this page.
  }
  if (changed) document.dispatchEvent(new CustomEvent(CART_GOODY_ITEMS_EVENT, { detail: { active: next } }));
}

/**
 * Does the cart hold Goody merchandise? Read by @theme/goody-fulfillment-recovery —
 * see CART_GOODY_ITEMS_KEY for why this is separate from isCartStoreActive().
 * @returns {boolean}
 */
export function isCartGoodyActive() {
  if (currentGoody !== null) return currentGoody;
  try {
    currentGoody = localStorage.getItem(CART_GOODY_ITEMS_KEY) === '1';
  } catch (e) {
    currentGoody = false;
  }
  return currentGoody;
}

/**
 * True when the cart holds store-fulfilled lines (not a fee, not Goody), names no store, and
 * none of its lines carries a complete fulfillment context — see CART_FULFILLMENT_PENDING_KEY.
 * Same `null` contract as the others: "couldn't tell" is never an unlock.
 *
 * Catering carts run their own store flow (assets/catering-cart.js) and are never pending.
 * Mirrored by the server seed in snippets/scripts.liquid.
 */
export function cartFulfillmentPending(cart) {
  if (!cart || !Array.isArray(cart.items)) return null;
  if (cart.items.some((item) => !item || typeof item !== 'object')) return null;
  const attributes = cart.attributes || {};
  if (String(attributes._is_catering_order ?? '') === 'true') return false;
  if (String(attributes.store_id ?? '').trim()) return false;
  const lines = cart.items.filter((item) => isStoreFulfilledLine(item));
  if (!lines.length) return false;
  return !lines.some((item) => isCompleteDeliveryContext(readLineDeliveryContext(item.properties)));
}

/** @type {boolean|null} Last known "products but no fulfillment". */
let currentPending = null;

/** Writes the pending state and announces it, but only when it actually changed. */
function applyPending(active) {
  const next = !!active;
  const changed = currentPending !== next;
  currentPending = next;
  try {
    localStorage.setItem(CART_FULFILLMENT_PENDING_KEY, next ? '1' : '0');
  } catch (e) {
    // Private mode / storage blocked — `currentPending` still holds for this page.
  }
  if (changed) document.dispatchEvent(new CustomEvent(CART_FULFILLMENT_PENDING_EVENT, { detail: { active: next } }));
}

/**
 * Does the cart hold products with no fulfillment chosen? Read by the header selector to stay
 * unlocked for that cart — see CART_FULFILLMENT_PENDING_KEY.
 * @returns {boolean}
 */
export function isCartFulfillmentPending() {
  if (currentPending !== null) return currentPending;
  try {
    currentPending = localStorage.getItem(CART_FULFILLMENT_PENDING_KEY) === '1';
  } catch (e) {
    currentPending = false;
  }
  return currentPending;
}

/** @type {Promise<void>|null} In-flight refresh, so overlapping signals share one read. */
let pending = null;

/** A signal arrived while a read was in flight — that read may predate it. */
let restale = false;

/**
 * Re-reads the cart and updates the state.
 *
 * Coalesced on a short delay: a single shopper action usually produces several
 * signals at once (our fetch hook AND the theme's `cart:update`), and they must
 * not turn into several `/cart.js` reads.
 *
 * A signal that lands while a read is already in flight can't be served by that
 * read — it may have been issued before the newer write committed. Rather than
 * drop it, it sets `restale` and one more read runs when this one settles, so the
 * state can't be left behind a mutation.
 * @returns {Promise<void>}
 */
export function refreshCartStoreActive() {
  if (pending) {
    restale = true;
    return pending;
  }
  pending = new Promise((resolve) => setTimeout(resolve, 120))
    .then(() => baseFetch('/cart.js', { headers: { Accept: 'application/json' } }))
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      const active = cartHasStoreItems(cart);
      // null = couldn't read it. Keep the last known state; never unlock on a guess.
      if (active !== null) apply(active);
      const any = cartHasAnyItems(cart);
      if (any !== null) applyAny(any);
      const goody = cartHasGoodyItems(cart);
      if (goody !== null) applyGoody(goody);
      // Same single read: the tip lifecycle ends where the merchandise does.
      endGratuityLifecycle(cart);
      // P7: and the cart's fulfillment, chosen or not (CART_FULFILLMENT_PENDING_KEY).
      const fulfillmentPending = cartFulfillmentPending(cart);
      if (fulfillmentPending !== null) applyPending(fulfillmentPending);
    })
    .catch(() => {
      // Cart unreachable — keep the last known state rather than unlocking.
    })
    .finally(() => {
      pending = null;
      if (restale) {
        restale = false;
        refreshCartStoreActive();
      }
    });
  return pending;
}

/**
 * Clears the cart and releases the lock. Mirrors the cart-conflict modal's own
 * clear (`/cart/clear.js` + reset attributes) so both paths leave the cart in the
 * same state. The store SELECTION is intentionally left alone — clearing the cart
 * is what unlocks changing it, not a reset of what was chosen.
 * @returns {Promise<void>}
 */
export async function clearCartForStoreChange() {
  // Deliberately unguarded: a failed clear must NOT unlock. Swallowing the error
  // and releasing the lock anyway would let the shopper change the store while
  // their items are still in the cart — the exact state the lock exists to stop.
  // The caller reports the failure and leaves the lock in place.
  const clear = await baseFetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
  if (!clear.ok) throw new Error(`cart clear HTTP ${clear.status}`);

  // `{ attributes: {} }` was a no-op — Shopify merges attribute writes per key, so only a
  // key sent as '' is removed. See clearedCartAttributesPayload for what that means here.
  const reset = await baseFetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clearedCartAttributesPayload()),
  });
  if (!reset.ok) throw new Error(`cart attribute reset HTTP ${reset.status}`);
  // The reset removed order_type and the store, so this clears the pickup preference.
  schedulePickupPointPreferenceSync();

  apply(false);
  applyAny(false);
  applyGoody(false);
  document.dispatchEvent(new CartUpdateEvent(null, 'ea-cart-lock'));
}

/* ── Detection ──────────────────────────────────────────────────────
 * Deliberately NOT wired to individual add / remove call sites: the cart is
 * written from a dozen modules and any new one would silently miss. Instead we
 * watch the cart endpoints themselves, which every one of them has to go
 * through, and back that up with the theme's own `cart:update` signal.
 */

window.fetch = async function eaCartLockFetch(input, init) {
  const response = await baseFetch(input, init);
  try {
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input?.url ?? '');
    // Always re-read the cart; never trust the write's own response body.
    // `/cart/change|update|clear` do reply with the cart, but this request passes
    // through several third-party fetch wrappers (Cordial, Dynamic Yield, Datadog,
    // the cart-conflict interceptor) before it reaches us, so the body we clone is
    // not reliably the cart — and a foreign payload that merely has an `items`
    // array read as "cart has items", locking the store on an empty cart. Reading
    // `/cart.js` through `baseFetch` goes straight to Shopify, and the refresh is
    // coalesced, so one shopper action still costs one request.
    // Not gated on `response.ok`: that status comes from the same object we already
    // decided not to trust. If a wrapper hands us a non-2xx for a write that did
    // commit, skipping the refresh would leave the lock stale. Re-reading `/cart.js`
    // is cheap, coalesced, and answers correctly either way — including for a write
    // that genuinely failed, where the cart simply comes back unchanged.
    if (CART_WRITE_RE.test(url)) refreshCartStoreActive();
  } catch (e) {
    // Never let bookkeeping break a cart request.
  }
  return response;
};

// Safety net for writers that captured `window.fetch` before us (the cart-conflict
// interceptor does). Coalescing means this costs nothing when our hook already saw it.
document.addEventListener(ThemeEvents.cartUpdate, () => refreshCartStoreActive());

// Another tab changed the cart — pick up its result without a request.
window.addEventListener('storage', (event) => {
  if (event.key === CART_STORE_ACTIVE_KEY) apply(event.newValue === '1');
  if (event.key === CART_ANY_ITEMS_KEY) applyAny(event.newValue === '1');
  if (event.key === CART_GOODY_ITEMS_KEY) applyGoody(event.newValue === '1');
  if (event.key === CART_FULFILLMENT_PENDING_KEY) applyPending(event.newValue === '1');
});

// Returning to a backgrounded tab, or a bfcache restore, can land on a stale cart.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') refreshCartStoreActive();
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartStoreActive();
});

// Server-rendered seed (snippets/scripts.liquid) — set before any module runs.
if (typeof window.__eaCartStoreActive === 'boolean') {
  apply(window.__eaCartStoreActive);
} else {
  isCartStoreActive(); // fall back to the persisted value
  refreshCartStoreActive();
}

if (typeof window.__eaCartAnyItems === 'boolean') {
  applyAny(window.__eaCartAnyItems);
} else {
  isCartNonEmpty(); // fall back to the persisted value
  refreshCartStoreActive(); // coalesced — costs nothing when the read above already ran
}

// The Goody seed matters more than the other two: @theme/goody-fulfillment-recovery
// completes its transition on a `false`, so a page that loads with Goody already gone
// has to hear about it. Seeded rather than left to `refreshCartStoreActive()` because
// the two branches above skip that read whenever the server-rendered seeds are present.
if (typeof window.__eaCartGoodyItems === 'boolean') {
  applyGoody(window.__eaCartGoodyItems);
} else {
  isCartGoodyActive(); // fall back to the persisted value
  refreshCartStoreActive(); // coalesced — costs nothing when a read above already ran
}

// P7: the header selector reads this on its first paint to decide whether a cart with
// products but no fulfillment opens unlocked (see CART_FULFILLMENT_PENDING_KEY).
// No read of its own when the seed is missing: the persisted value stands until the next
// refresh, which every cart write, tab return and bfcache restore already triggers.
if (typeof window.__eaCartFulfillmentPending === 'boolean') {
  applyPending(window.__eaCartFulfillmentPending);
} else {
  isCartFulfillmentPending(); // fall back to the persisted value
}

// THE LIFECYCLE CHECK HAS TO RUN ON LOAD — EA-3697, QA finding 1 (15 Sep 2026).
//
// `endGratuityLifecycle` is reached only from `refreshCartStoreActive`, and every
// branch above skips that read whenever the server-rendered seeds are present —
// which they always are. So the check ran on cart WRITES and never on a cart the
// page merely LOADED with.
//
// That is not an edge case, it is the normal way the orphan state arrives: the
// gratuity line and the `_tip_*` decision are both written at CHECKOUT, so a
// shopper who empties their cart there and comes back to the storefront lands on
// a plain navigation with no cart write for the hook to see. Confirmed live on
// myedible, 15 Sep 2026 — a cart holding only "Gratuity" plus `_tip_amount: "900"`
// survived a full load of /pages/contact untouched, with the ZIP and store locked
// by a cart that had nothing in it.
//
// Gated on a fourth seed rather than read unconditionally: a /cart.js on every
// page load is the cost the other three seeds exist to avoid, and only a cart
// already in the orphan state needs looking at. The seed says "look" and nothing
// more — the DECISION is still made from the authoritative /cart.js read inside
// the refresh, so the "never end a lifecycle on a guess" contract holds.
if (window.__eaCartGratuityOrphan === true) refreshCartStoreActive();

// ── 1-Hour ⇄ Same-Day cart eligibility (EA-3873) ─────────────────────────────────────
//
// ONE reading of "may this committed cart move to / stay on 1-Hour", shared by the three
// surfaces that decide it: the PDP dropdown (@theme/pdp-availability), the personalization
// drawer's in-drawer override (@theme/personalization-drawer) and the add-time convergence in
// @theme/cart-conflict-modal. They used to carry three copies of the loop below and read the
// `_item_one_hour_eligible` stamp strictly, so a line the drawer mis-stamped 'false' (it read
// v4's `oneHour` flag with `=== true`) froze a genuinely dual-eligible cart on Same Day in all
// three at once. The rule now:
//
//   - EA-3048: a line's verdict comes from today's fast5 delivery entry for its external variant
//     id when one is known — `oneHourDeliveryEligible`, read through the shared
//     `isOneHourDeliveryEligible` (plus the entry's `available` / selected-store check). A fresh
//     entry WINS over the `_item_one_hour_eligible` stamp, which older adds wrote from the
//     batch-dependent `oneHour` field. The stamp is only the fallback when no entry is known.
//   - Neither source proves it → NOT capable. Unknown lines keep the cart conservative.
//   - The 1-Hour ⇄ Same-Day SWITCH (oneHourSwitchEligible) additionally needs the cart to hold
//     at most ONE_HOUR_SWITCH_MAX_CART_PARENTS parent items.
//   - 1-Hour-capable implies Same-Day-capable. Same-Day eligibility is never derived here.
//   - Only PARENT lines are judged: the stamp lives on the parent line only, so its add-on /
//     upgrade / CYO / companion lines (which follow their parent's window) would otherwise
//     read as "unknown" and freeze every personalised cart.

/**
 * Most parent items (total quantity) a committed cart may hold for the 1-Hour ⇄ Same-Day
 * switch to be offered (EA-3048). With the incoming item this stays within the cart-conflict
 * modal's 1-Hour cap of 3.
 */
export const ONE_HOUR_SWITCH_MAX_CART_PARENTS = 2;

/**
 * Hard cap on the total QUANTITY of store-fulfilled parent items a cart may hold at 1-Hour
 * (EA-3048). MUST match the cart-conflict interceptor's ONE_HOUR_LIMIT — both read it, so it
 * lives here (the shared cart-lock module) as the single source of truth.
 */
export const ONE_HOUR_LIMIT = 3;

/**
 * Today's v4 delivery entries most recently seen by a surface, so a reader that has no
 * response of its own (the conflict modal, at /cart/add time) judges lines by the same
 * answer the dropdown that armed the switch was judging them by.
 * @type {{date: string, storeId: number|string|null, byId: Map<number, any>}|null}
 */
let rememberedTodayEntries = null;

/**
 * Normalise an entries source into Map<externalId, entry>.
 * @param {Map<number, any>|Array<any>|null|undefined} entries
 * @returns {Map<number, any>|null}
 */
function toEntryMap(entries) {
  if (!entries) return null;
  if (entries instanceof Map) return entries;
  if (!Array.isArray(entries)) return null;
  const map = new Map();
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const id = Number(entry.productId != null ? entry.productId : entry.id);
    if (!Number.isNaN(id)) map.set(id, entry);
  }
  return map;
}

/**
 * Record today's v4 DELIVERY entries for the selected store (see rememberedTodayEntries).
 * Merges into what is already held for the same date + store, so the PDP's answer and the
 * drawer's answer for another product both count; a different date/store replaces it.
 * @param {Map<number, any>|Array<any>|null|undefined} entries today's v4 `delivery[]` (or a Map by external id)
 * @param {{storeId?: number|string|null, date?: string}} [ctx]
 */
export function rememberTodayDeliveryEntries(entries, ctx = {}) {
  const byId = toEntryMap(entries);
  if (!byId || byId.size === 0) return;
  const date = ctx.date || todayIso();
  if (date !== todayIso()) return;
  const storeId = ctx.storeId ?? null;
  const same =
    rememberedTodayEntries &&
    rememberedTodayEntries.date === date &&
    String(rememberedTodayEntries.storeId ?? '') === String(storeId ?? '');
  const merged = same ? new Map(rememberedTodayEntries.byId) : new Map();
  for (const [id, entry] of byId) merged.set(id, entry);
  rememberedTodayEntries = { date, storeId, byId: merged };
}

/**
 * Whether a line is one the 1-Hour cart eligibility is judged on: a store-fulfilled PARENT line.
 * @param {{properties?: Record<string, unknown>}|null|undefined} item a /cart.js line
 * @returns {boolean}
 */
export function isOneHourJudgedLine(item) {
  if (!item || !isStoreFulfilledLine(item)) return false;
  return String(item.properties?.[CART_PROPERTY_KEYS.itemType] || '') === CART_ITEM_TYPES.parent;
}

/**
 * Would adding this product mix SHIPMENT-ONLY and REGULAR products in one cart?
 *
 * A shipment-only product (its only availability method is Shipment) and a regular product
 * (ships AND delivers/picks up) are incompatible fulfillment kinds, the same way a shipment-only
 * product and a delivery/pickup cart are. That case never reaches here — the cart's fulfillment
 * type already differs, and both surfaces refuse it on that alone. This is the case the type
 * can't see: a SHIPMENT cart, where a regular product shipped and a shipment-only product are
 * both stamped 'shipment'. The `_item_shipment_only` stamp on each parent line tells them apart.
 *
 * Only store-fulfilled parent lines are judged (add-ons, cards and Goody lines say nothing about
 * the product's kind). A line without the stamp — added before it existed — is skipped, so an
 * old cart degrades to the previous behaviour rather than blocking on a guess.
 *
 * @param {{items?: Array<any>}|null|undefined} cart cart JSON from /cart.js
 * @param {boolean} incomingShipmentOnly whether the product being added is shipment-only
 * @returns {boolean}
 */
export function cartShipmentMixConflict(cart, incomingShipmentOnly) {
  for (const item of cart?.items || []) {
    if (!isOneHourJudgedLine(item)) continue;
    const stamp = String(item.properties?.[CART_PROPERTY_KEYS.itemShipmentOnly] ?? '');
    if (stamp !== 'true' && stamp !== 'false') continue;
    if ((stamp === 'true') !== Boolean(incomingShipmentOnly)) return true;
  }
  return false;
}

/**
 * Can this cart line be delivered 1-Hour at the selected store today?
 *
 * EA-3048: when today's fast5 delivery entry for its `_external_variant_id` is known, that entry
 * decides — `available`, the selected store in `stores[]`, and `oneHourDeliveryEligible === true`
 * (isOneHourDeliveryEligible). The `_item_one_hour_eligible` stamp is consulted only when no entry
 * is known. False when neither proves it.
 *
 * @param {{properties?: Record<string, unknown>}|null|undefined} item a /cart.js line
 * @param {Map<number, any>|Array<any>|null|undefined} todayEntries today's v4 delivery entries;
 *   omitted/null → the entries last passed to rememberTodayDeliveryEntries (same store only)
 * @param {{storeId?: number|string|null}} [ctx]
 * @returns {boolean}
 */
export function lineOneHourCapable(item, todayEntries, ctx = {}) {
  const props = item?.properties || {};
  const stampedCapable = String(props[CART_PROPERTY_KEYS.itemOneHourEligible]) === 'true';

  const extId = Number(props[CART_PROPERTY_KEYS.externalVariantId]);
  if (!props[CART_PROPERTY_KEYS.externalVariantId] || Number.isNaN(extId)) return stampedCapable;

  let storeId = ctx.storeId ?? null;
  let byId = toEntryMap(todayEntries);
  if (!byId && rememberedTodayEntries && rememberedTodayEntries.date === todayIso()) {
    // Only trust the remembered answer for the store it was asked about.
    if (storeId == null || String(rememberedTodayEntries.storeId ?? '') === String(storeId)) {
      byId = rememberedTodayEntries.byId;
      storeId = storeId ?? rememberedTodayEntries.storeId;
    }
  }
  const entry = byId ? byId.get(extId) : null;
  // No fresh answer for this line → the add-time stamp is all there is.
  if (!entry) return stampedCapable;
  // The shared reading: `available`, the selected store in `stores[]`, and — for 1-Hour — the
  // fast5 `oneHourDeliveryEligible` flag (isOneHourDeliveryEligible, @theme/ea-availability).
  return entryAvailableFor(entry, { fulfillmentType: 'delivery', deliveryType: 'one_hour', storeId });
}

/**
 * The committed cart's delivery-speed state, as the 1-Hour ⇄ Same-Day interplay reads it.
 *
 * - committedType: the `_item_delivery_type` every store-fulfilled line agrees on, '' when they
 *   disagree or none carries one.
 * - allOneHourEligible: at least one judged (parent) line, and every one is lineOneHourCapable.
 * - parentCount: total QUANTITY of store-fulfilled parent lines (the 1-Hour cap denominator).
 * - oneHourSwitchEligible (EA-3048): the cart may take the 1-Hour ⇄ Same-Day switch — committed to
 *   'same_day' or 'one_hour', parentCount ≤ ONE_HOUR_SWITCH_MAX_CART_PARENTS, and allOneHourEligible.
 *   The CURRENT variant's own eligibility is the calling surface's half of the rule.
 *
 * @param {{items?: Array<any>}|null|undefined} cart cart JSON from /cart.js
 * @param {{todayEntries?: Map<number, any>|Array<any>|null, storeId?: number|string|null}} [options]
 * @returns {{committedType: string, allOneHourEligible: boolean, parentCount: number, oneHourSwitchEligible: boolean}}
 */
export function computeCartLockInfo(cart, { todayEntries = null, storeId = null } = {}) {
  const lines = (cart?.items || []).filter((it) => it && isStoreFulfilledLine(it));
  const types = new Set();
  let judged = 0;
  let allCapable = true;
  let parentCount = 0;
  for (const it of lines) {
    const props = it.properties || {};
    const type = String(props[CART_PROPERTY_KEYS.itemDeliveryType] || props.item_delivery_type || '');
    if (type) types.add(type);
    if (isOneHourJudgedLine(it)) {
      parentCount += Number(it.quantity) || 0;
      judged++;
      if (allCapable && !lineOneHourCapable(it, todayEntries, { storeId })) allCapable = false;
    }
  }
  // Resolve the cart's effective committed window:
  //   - one agreed type                              → that type
  //   - lines split ONLY between 1-Hour and Same Day → 'same_day', the coherent SUPERSET
  //     (1-Hour ⊆ Same-Day, EA-3874). A cart holding a 1-Hour line AND a Same-Day line CAN
  //     all fulfil Same Day, so it stays reversible-lockable instead of reading as
  //     "mixed / no window" — which is what froze the 1-Hour ⇄ Same-Day toggle on both the
  //     PDP dropdown and the PDrawer selector for exactly this scenario. The interplay then
  //     lets the shopper upgrade the whole cart to 1-Hour (all lines are 1-Hour-capable).
  //   - any other disagreement (a next-day / scheduled / pickup type in the mix) → '' (fully locked)
  let committedType = '';
  if (types.size === 1) {
    committedType = [...types][0];
  } else if (types.size > 1 && [...types].every((t) => t === 'one_hour' || t === 'same_day')) {
    committedType = 'same_day';
  }
  const allOneHourEligible = judged > 0 && allCapable;
  const oneHourSwitchEligible =
    (committedType === 'same_day' || committedType === 'one_hour') &&
    parentCount <= ONE_HOUR_SWITCH_MAX_CART_PARENTS &&
    allOneHourEligible;
  // EA-3048: the 1-Hour cap is reached when the cart is otherwise reversible-eligible (committed to
  // a store window, every line 1-Hour-capable) but adding ONE more parent as 1-Hour would exceed
  // ONE_HOUR_LIMIT. In that state the 1-Hour ⇄ Same-Day toggle can't offer 1-Hour anymore — but the
  // Same-Day DOWNGRADE has no cap, so the surfaces still offer Same Day (disabling 1-Hour, defaulting
  // to Same Day) instead of freezing the whole dropdown. Mutually exclusive with oneHourSwitchEligible
  // (that needs parentCount ≤ 2; this needs parentCount + 1 > 3, i.e. parentCount ≥ 3).
  const oneHourLimitReached =
    (committedType === 'same_day' || committedType === 'one_hour') &&
    allOneHourEligible &&
    parentCount + 1 > ONE_HOUR_LIMIT;
  return { committedType, allOneHourEligible, parentCount, oneHourSwitchEligible, oneHourLimitReached };
}
